/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

/**
 * Client-side registry of nonce → message id pairs observed on successful POST
 * /channels/{id}/messages responses. The server echoes the nonce on the send
 * response and on the WS MESSAGE_CREATE event, but GET message rows strip it —
 * so fetched rows are annotated from this registry to let fetch merges bridge
 * an in-flight optimistic message (id = nonce) onto its real row (id =
 * snowflake) instead of rendering both.
 */

const MAX_RECORDED_NONCES = 500;

const nonceByMessageKey = new Map<string, string>();

const messageKey = (channelId: string, messageId: string): string => `${channelId}:${messageId}`;

export function recordMessageNonce(channelId: string, nonce: string, messageId: string): void {
	if (!channelId || !nonce || !messageId) return;

	nonceByMessageKey.set(messageKey(channelId, messageId), nonce);

	if (nonceByMessageKey.size > MAX_RECORDED_NONCES) {
		const oldest = nonceByMessageKey.keys().next().value;
		if (oldest != null) {
			nonceByMessageKey.delete(oldest);
		}
	}
}

export function getRecordedNonce(channelId: string, messageId: string): string | null {
	return nonceByMessageKey.get(messageKey(channelId, messageId)) ?? null;
}
